// Checkout embedded (Stripe Payment Element) per il carrello unificato multi-partner.
// Crea un PaymentIntent sulla piattaforma, mostra il Payment Element e, al successo,
// finalizza l'ordine via /checkout/confirm (fallback affidabile al webhook).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CreditCard, Loader2, ShieldCheck, Lock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { marketplacePaymentsService } from '@/services/marketplacePayments.service';
import { formatApiError } from '@/lib/api';
import { money, currencyLabel } from '@/lib/money';

const eur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));

// legge la CSS var --primary (formato "H S% L%") e la converte in hsl() per Stripe
function themePrimary() {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue('--primary').trim();
    if (v) return `hsl(${v})`;
  } catch (_) { /* noop */ }
  return '#2b5c48';
}

// Cache dei Stripe object per publishable key (loadStripe va chiamato una sola volta per pk)
const stripeCache = {};
function getStripe(pk) {
  if (!pk) return null;
  if (!stripeCache[pk]) stripeCache[pk] = loadStripe(pk);
  return stripeCache[pk];
}

function PaymentForm({ orderId, amountLabel, onSuccess, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'Controlla i dati della carta.');
        setProcessing(false);
        return;
      }
      const { error: payError, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/cliente/carrello`,
        },
      });
      if (payError) {
        setError(payError.message || 'Pagamento non riuscito. Riprova o usa un\'altra carta.');
        setProcessing(false);
        return;
      }
      if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
        // finalizza l'ordine lato server (idempotente)
        try {
          const res = await marketplacePaymentsService.checkoutConfirm(orderId);
          if (res?.status === 'succeeded' && (res?.order?.id || res?.order_id)) {
            // usa l'ID reale dell'ordine (platform_orders), non l'ID del pending checkout
            onSuccess(res.order?.id || res.order_id);
            return;
          }
          // pagamento in elaborazione: informo e chiudo
          toast.info('Pagamento in elaborazione. Riceverai la conferma a breve.');
          onClose();
        } catch (confErr) {
          setError(formatApiError(confErr?.response?.data?.detail) || 'Pagamento riuscito ma finalizzazione non completata. Contatta il supporto.');
          setProcessing(false);
        }
        return;
      }
      setError('Stato pagamento inatteso. Riprova.');
      setProcessing(false);
    } catch (err) {
      setError('Errore imprevisto durante il pagamento. Riprova.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handlePay} className="space-y-4" data-testid="stripe-payment-form">
      {!ready && <Skeleton className="h-40 w-full rounded-xl" data-testid="stripe-element-loading" />}
      <div className={ready ? '' : 'hidden'}>
        <PaymentElement onReady={() => setReady(true)} options={{ layout: 'tabs' }} />
      </div>

      {error && (
        <Alert variant="destructive" data-testid="stripe-payment-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button type="submit" className="w-full gap-2" size="lg"
        disabled={!stripe || !ready || processing} data-testid="stripe-pay-button">
        {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
        {processing ? 'Elaborazione…' : `Paga ${amountLabel}`}
      </Button>

      <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5" /> Pagamento protetto da Stripe
      </div>
    </form>
  );
}

export function StripeCheckoutDialog({ open, onOpenChange, redeem = 0, shippingAddress = null,
                                       totalLabel, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [initError, setInitError] = useState('');
  const [intent, setIntent] = useState(null); // {client_secret, publishable_key, order_id, amount}
  const startedRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (!open) {
      if (intent?.order_id) marketplacePaymentsService.checkoutCancel(intent.order_id).catch(() => {});
      setIntent(null);
      setInitError('');
      startedRef.current = false;
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      setLoading(true);
      setInitError('');
      try {
        const res = await marketplacePaymentsService.checkoutIntent(redeem, shippingAddress);
        if (!openRef.current) {
          await marketplacePaymentsService.checkoutCancel(res.order_id);
          return;
        }
        setIntent(res);
      } catch (e) {
        setInitError(formatApiError(e?.response?.data?.detail) || 'Impossibile avviare il pagamento.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stripePromise = useMemo(() => getStripe(intent?.publishable_key), [intent?.publishable_key]);
  const options = useMemo(() => (intent?.client_secret ? {
    clientSecret: intent.client_secret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: themePrimary(),
        borderRadius: '10px',
        fontFamily: 'Figtree, system-ui, sans-serif',
      },
    },
  } : null), [intent?.client_secret]);

  const amountLabel = intent?.amount ? money(intent.amount / 100, intent?.currency) : (totalLabel || '');
  const isForeignCcy = intent?.currency && String(intent.currency).toLowerCase() !== 'eur';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-md" data-testid="stripe-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" /> Completa il pagamento
          </DialogTitle>
          <DialogDescription>
            Un unico pagamento per tutto il carrello. Gli importi vengono ripartiti automaticamente
            tra gli operatori, al netto della commissione della piattaforma.
          </DialogDescription>
        </DialogHeader>

        <div className="-mr-2 flex-1 overflow-y-auto pr-2" data-testid="stripe-checkout-scroll">
        {loading && (
          <div className="space-y-3" data-testid="stripe-intent-loading">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-40 w-full rounded-xl" />
            <Skeleton className="h-11 w-full rounded-lg" />
          </div>
        )}

        {!loading && initError && (
          <Alert variant="destructive" data-testid="stripe-init-error">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{initError}</AlertDescription>
          </Alert>
        )}

        {!loading && !initError && intent && stripePromise && options && (
          <Elements stripe={stripePromise} options={options}>
            
            <PaymentForm
              orderId={intent.order_id}
              amountLabel={amountLabel}
              onSuccess={onSuccess}
              onClose={() => onOpenChange(false)}
            />
          </Elements>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

